import { createClient, type Session } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PROJECT_URL_SUPABASE;
const supabaseAnonKey = import.meta.env.VITE_ANON_PUBLIC_SUPABASE;

export type AuthCallbackError = "expired" | "used" | "unreadable";

export type AuthCallbackSnapshot = {
  path: string;
  type: string | null;
  hasImplicitTokens: boolean;
  userId: string | null;
  error: AuthCallbackError | null;
};

function decodeJwtSubject(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")));
    return typeof decoded.sub === "string" ? decoded.sub : null;
  } catch {
    return null;
  }
}

function captureAuthCallback(): AuthCallbackSnapshot {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search.replace(/^\?/, ""));
  const value = (key: string) => hash.get(key) || query.get(key);
  const errorText = `${value("error_code") || ""} ${value("error") || ""} ${value("error_description") || ""}`.trim().toLowerCase();
  const error = errorText.includes("already") || errorText.includes("used")
    ? "used"
    : errorText.includes("expired") || errorText.includes("otp_expired")
      ? "expired"
      : errorText
        ? "unreadable"
        : null;
  const accessToken = hash.get("access_token");

  return {
    path: window.location.pathname,
    type: value("type"),
    hasImplicitTokens: Boolean(accessToken && hash.get("refresh_token")),
    userId: decodeJwtSubject(accessToken),
    error,
  };
}

// Capture callback provenance before Supabase consumes and clears the URL fragment.
// Token values are intentionally discarded and must never be logged or persisted.
export const authCallbackSnapshot = captureAuthCallback();

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

const expectedCallbackEvent = authCallbackSnapshot.type === "recovery"
  ? "PASSWORD_RECOVERY"
  : authCallbackSnapshot.type === "invite"
    ? "SIGNED_IN"
    : null;

// Register immediately after client creation so the callback-specific event
// emitted during Supabase initialization cannot be missed by page effects.
export const validatedAuthCallbackSession: Promise<Session | null> = (
  authCallbackSnapshot.error ||
  !authCallbackSnapshot.hasImplicitTokens ||
  !authCallbackSnapshot.userId ||
  !expectedCallbackEvent
)
  ? Promise.resolve(null)
  : new Promise((resolve) => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === expectedCallbackEvent && session?.user.id === authCallbackSnapshot.userId) {
        resolve(session);
        queueMicrotask(() => data.subscription.unsubscribe());
      }
    });
  });
